// Una "jornada" es un día de juego de la rueda: todas las fechas (una por zona)
// que se juegan la misma fecha calendario. El modelo guarda las fechas colgando
// de cada zona, así que para las planillas que miran la rueda entera (micrófono)
// hay que volver a juntarlas por día.
//
// Dentro de una jornada puede haber uno o dos turnos (mañana y tarde). El turno
// no se carga en ningún lado: se deduce de los horarios (ver `turnosDeJornada`).

import { fechaCalendario, numerosDeFecha } from "./fechas";
import { agruparPorCancha, enMinutos, SIN_CANCHA } from "./horarios";

const masRepetido = (valores = []) => {
  const conteo = new Map();
  valores.forEach((v) => conteo.set(v, (conteo.get(v) || 0) + 1));
  let ganador = null;
  let maximo = 0;
  conteo.forEach((cantidad, valor) => {
    if (cantidad > maximo) {
      maximo = cantidad;
      ganador = valor;
    }
  });
  return ganador;
};

// [{ dia, numero, sede, fechas, partidos }] de la más reciente a la más vieja.
// Solo entran las fechas que ya tienen día calendario asignado.
// `numero` y `sede` salen de lo que declara la mayoría de las zonas (en la
// práctica todas coinciden: es la misma jornada del torneo).
// `fechas` son las fechas del día, una por zona, con el número que le toca dentro
// de su zona. Los partidos vienen anotados con la zona a la que pertenecen, que es
// lo que después permite repartirlos por turno.
export function jornadasDeRueda(round) {
  const zones = (round && round.zones) || [];
  const porDia = new Map();

  zones.forEach((zone) => {
    const numeros = numerosDeFecha(zone.matchDates || []);
    (zone.matchDates || []).forEach((md) => {
      if (!md.date) return;
      if (!porDia.has(md.date)) porDia.set(md.date, []);
      porDia.get(md.date).push({ zone, matchDate: md, numero: numeros[md.id] || null });
    });
  });

  return [...porDia.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([dia, entradas]) => ({
      dia,
      numero: masRepetido(entradas.map((e) => e.numero).filter(Boolean)),
      sede: masRepetido(entradas.map((e) => e.matchDate.sede).filter(Boolean)),
      fechas: entradas,
      partidos: entradas.flatMap((e) =>
        (e.matchDate.matches || []).map((m) => ({ ...m, zoneId: e.zone.id, zoneName: e.zone.name }))
      ),
    }));
}

// Rótulo de la jornada para los selectores: "Fecha 3 · Domingo, 30 de agosto".
export function etiquetaDeJornada(jornada) {
  return [jornada.numero ? "Fecha " + jornada.numero : "Fecha", fechaCalendario({ date: jornada.dia })]
    .filter(Boolean)
    .join(" · ");
}

// Números de cancha que se usan en la jornada, ordenados. Los partidos que
// todavía no tienen cancha asignada no cuentan (no hay cartel que colgar).
export function canchasDeJornada(partidos = []) {
  return agruparPorCancha(partidos)
    .filter((grupo) => grupo.clave !== SIN_CANCHA)
    .map((grupo) => grupo.clave);
}

// El turno mañana arranca antes de las 12:00 y el tarde nunca antes de las 13:00,
// así que cualquier turno que empiece antes de las 12:30 es de mañana. Sirve para
// las dos preguntas: si la jornada tiene mañana, y cómo se llama un turno único.
const LIMITE_MANANA = 12 * 60 + 30;
// Y como el turno tarde arranca entre las 13:00 y las 15:00, el corte entre
// turnos cae siempre en esa franja (con un poco de margen).
const CORTE_HASTA = 15 * 60 + 30;
const ESPACIADO_POR_DEFECTO = 20;

// Un corte sirve si deja una mañana de un lado y una tarde del otro.
const esCorteValido = (inicioDeLaJornada, corte) =>
  inicioDeLaJornada < LIMITE_MANANA && corte >= LIMITE_MANANA && corte <= CORTE_HASTA;

const horariosOrdenados = (partidos = []) =>
  [...new Set(partidos.map((m) => enMinutos(m.hora)).filter((m) => m != null))].sort((a, b) => a - b);

// { [zoneId]: { inicio, fin } } con el primer y el último partido de cada zona.
// Las zonas sin horarios cargados quedan afuera.
function rangosPorZona(partidos = []) {
  const rangos = new Map();
  partidos.forEach((m) => {
    const minutos = enMinutos(m.hora);
    if (minutos == null) return;
    const rango = rangos.get(m.zoneId);
    if (!rango) rangos.set(m.zoneId, { inicio: minutos, fin: minutos });
    else {
      rango.inicio = Math.min(rango.inicio, minutos);
      rango.fin = Math.max(rango.fin, minutos);
    }
  });
  return rangos;
}

// Espaciado típico de la grilla (20 o 25 minutos). Se mide dentro de cada cancha,
// que es donde la serie de horarios es regular, y se toma el más repetido.
function espaciadoTipico(partidos = []) {
  const porCancha = new Map();
  partidos.forEach((m) => {
    const minutos = enMinutos(m.hora);
    if (minutos == null) return;
    const clave = String(m.zoneId || "") + "|" + String(m.cancha || "");
    if (!porCancha.has(clave)) porCancha.set(clave, []);
    porCancha.get(clave).push(minutos);
  });

  const espaciados = [];
  porCancha.forEach((horarios) => {
    const ordenados = [...new Set(horarios)].sort((a, b) => a - b);
    for (let i = 1; i < ordenados.length; i++) espaciados.push(ordenados[i] - ordenados[i - 1]);
  });

  return masRepetido(espaciados) || ESPACIADO_POR_DEFECTO;
}

// Indicador 1 (el fuerte): cada zona juega en un único turno, así que el corte
// tiene que poder partir las zonas en dos grupos que no se pisan entre sí. Se
// prueban todos los cortes posibles (las zonas ordenadas por su primer partido) y
// se elige el que deja el hueco más grande entre el final de la mañana y el
// arranque de la tarde. Acá no se le pide al hueco que sea grande: alcanza con que
// las zonas no se superpongan y con que el corte caiga en la franja del cambio.
function corteEntreZonas(partidos = []) {
  const rangos = [...rangosPorZona(partidos).values()].sort((a, b) => a.inicio - b.inicio);
  if (rangos.length < 2) return null;

  let corte = null;
  let mayor = 0;
  let finDeLaManana = rangos[0].fin;
  for (let i = 1; i < rangos.length; i++) {
    const hueco = rangos[i].inicio - finDeLaManana;
    if (hueco > mayor && esCorteValido(rangos[0].inicio, rangos[i].inicio)) {
      mayor = hueco;
      corte = rangos[i].inicio;
    }
    finDeLaManana = Math.max(finDeLaManana, rangos[i].fin);
  }
  return corte;
}

// Indicador 2 (el de respaldo, para cuando las zonas se pisan o hay una sola):
// los partidos se espacian periódicamente (cada 20 o 25 minutos) y el cambio de
// turno se ve como un salto más grande que ese espaciado.
function corteEntreHorarios(partidos = []) {
  const horarios = horariosOrdenados(partidos);
  if (horarios.length < 2) return null;

  const espaciado = espaciadoTipico(partidos);
  let corte = null;
  let mayor = espaciado;
  for (let i = 1; i < horarios.length; i++) {
    const salto = horarios[i] - horarios[i - 1];
    if (salto > mayor && esCorteValido(horarios[0], horarios[i])) {
      mayor = salto;
      corte = horarios[i];
    }
  }
  return corte;
}

// Minuto en el que arranca el turno tarde, o null si la jornada tiene un solo turno.
function corteDeTurnos(partidos = []) {
  const porZonas = corteEntreZonas(partidos);
  return porZonas != null ? porZonas : corteEntreHorarios(partidos);
}

// [{ id, nombre, partidos }] con uno o dos turnos. Cada turno sale después en su
// propia hoja de la planilla.
//
// El reparto se hace por zona, no por partido: cada zona juega en un único turno,
// así que va entera al turno de su primer partido (y sus partidos sin hora la
// acompañan). Si una zona quedara a caballo del corte —no debería pasar, pero los
// horarios se cargan a mano— esa zona sí se reparte partido por partido.
export function turnosDeJornada(partidos = []) {
  if (partidos.length === 0) return [];

  const corte = corteDeTurnos(partidos);
  if (corte == null) {
    const horarios = horariosOrdenados(partidos);
    const esManana = horarios.length === 0 || horarios[0] < LIMITE_MANANA;
    return [{ id: esManana ? "manana" : "tarde", nombre: esManana ? "Turno Mañana" : "Turno Tarde", partidos }];
  }

  const rangos = rangosPorZona(partidos);
  // "manana" | "tarde", o null si la zona cruza el corte y hay que mirar partido a partido.
  const turnoDeZona = (zoneId) => {
    const rango = rangos.get(zoneId);
    if (!rango) return "manana"; // zona sin horarios cargados: al primer turno
    if (rango.fin < corte) return "manana";
    if (rango.inicio >= corte) return "tarde";
    return null;
  };

  const manana = [];
  const tarde = [];
  partidos.forEach((m) => {
    let turno = turnoDeZona(m.zoneId);
    if (turno == null) {
      const minutos = enMinutos(m.hora);
      turno = minutos != null && minutos >= corte ? "tarde" : "manana";
    }
    (turno === "tarde" ? tarde : manana).push(m);
  });

  return [
    { id: "manana", nombre: "Turno Mañana", partidos: manana },
    { id: "tarde", nombre: "Turno Tarde", partidos: tarde },
  ].filter((t) => t.partidos.length > 0);
}
